"use client";

import { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { format, formatDuration, parseISO } from "date-fns";
import i18n from "i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Icon } from "@iconify-icon/react";
import { CalendarIcon } from "lucide-react";
import { useDebouncedCallback } from "use-debounce";

import { toast } from "@/utils/customToast";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCalendarModalStore } from "@/features/calendar/store/useCalendarModalStore";
import { currentLanguageLocale, getDateFnsLocale } from "@/i18n/getDateFnsLocale.ts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore.tsx";
import { WORKSPACES_API_ENDPOINTS } from "@/constants/api.ts";
import { axiosApiInstance } from "@/libs/axios";
import { useCalendarStore } from "@/features/calendar/store/useCalendarStore.tsx";
import { calculateLessonPrice } from "@/features/calendar/api/Pricing";
import GenericLoader from "@/components/GenericLoader.tsx";
import { SubjectWithLevels } from "@/features/calendar/api/Slots.ts";
import { cn } from "@/lib/utils";

const MotionCard = motion.create(Card);

interface PriceData {
  currency: string;
  currency_symbol: string;
  subtotal: number;
  subtotal_formatted: string;
  service_fee: number;
  service_fee_formatted: string;
  total: number;
  total_formatted: string;
  duration_minutes: number;
  hourly_rate: number;
  hourly_rate_formatted: string;
}

// Remove the TreeSubjectSelector component entirely and use regular Select in the form

export function SlotReservationContent() {
  const { t } = useTranslation(["forms", "common"]);
  const { slot, setIsOpen, subjectsWithLevels } = useCalendarModalStore();
  const { setToReload } = useCalendarStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [priceData, setPriceData] = useState<PriceData | null>(null);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const { data: workspaceInformation } = useWorkspaceInformationStore();

  // Flatten subjects for select dropdown
  const flattenedSubjects = useMemo(() => {
    const flattened: Array<{
      subject: SubjectWithLevels["subject"];
      isChild: boolean;
      isSelectable: boolean;
    }> = [];

    subjectsWithLevels?.forEach((parentSubject) => {
      // Add parent subject
      flattened.push({
        subject: parentSubject.subject,
        isChild: false,
        isSelectable: parentSubject.is_selectable,
      });

      // Add children with indentation flag
      parentSubject.children?.forEach((childSubject) => {
        flattened.push({
          subject: childSubject.subject,
          isChild: true,
          isSelectable: childSubject.is_selectable,
        });
      });
    });

    return flattened;
  }, [subjectsWithLevels]);

  const slotReservationSchema = useMemo(
    () =>
      z.object({
        topic: z.string().max(255, t("forms:errors.topicMax")).optional(),
        context: z.string().max(500, t("forms:errors.contextMax")).optional(),
        schedule_calendar_slot_id: z.string(),
        subject_id: z
          .string({
            message: t("forms:errors.subjectRequired"),
            required_error: t("forms:errors.subjectRequired"),
          })
          .min(1, { message: t("forms:errors.subjectRequired") }),
        curriculum_level_id: z
          .string({
            message: t("forms:errors.curriculumLevelRequired"),
            required_error: t("forms:errors.curriculumLevelRequired"),
          })
          .min(1, { message: t("forms:errors.curriculumLevelRequired") }),
        acceptTerms: z.boolean().refine((val) => val, {
          message: t("forms:errors.acceptTermsRequired"),
        }),
      }),
    [i18n.language],
  );

  type SlotReservationFormData = z.infer<typeof slotReservationSchema>;

  const form = useForm<SlotReservationFormData>({
    resolver: zodResolver(slotReservationSchema),
    defaultValues: {
      topic: "",
      context: "",
      schedule_calendar_slot_id: slot?.id || "",
      subject_id: "",
      curriculum_level_id: "",
      acceptTerms: false,
    },
  });

  const subjectId = form.watch("subject_id");
  const curriculumLevelId = form.watch("curriculum_level_id");

  // Get available curriculum levels for selected subject
  const availableCurriculumLevels = useMemo(() => {
    if (!subjectId || !subjectsWithLevels) return [];

    const findSubjectData = (items: SubjectWithLevels[]): SubjectWithLevels | null => {
      for (const item of items) {
        if (item.subject.id === subjectId) return item;
        if (item.children) {
          const found = findSubjectData(item.children);

          if (found) return found;
        }
      }

      return null;
    };

    const subjectData = findSubjectData(subjectsWithLevels);

    return subjectData?.curriculum_levels || [];
  }, [subjectId, subjectsWithLevels]);

  // Reset curriculum level when subject changes
  useEffect(() => {
    form.setValue("curriculum_level_id", "");
  }, [subjectId]);

  useEffect(() => {
    if (slot) {
      form.setValue("schedule_calendar_slot_id", slot.id);
    }
  }, [slot]);

  const fetchPrice = useDebouncedCallback(async () => {
    if (!subjectId || !curriculumLevelId || !slot || !workspaceInformation) return;

    try {
      const response = await calculateLessonPrice(workspaceInformation.id, {
        schedule_calendar_slot_id: slot.id,
        subject_id: subjectId,
        curriculum_level_id: curriculumLevelId,
      });

      setPriceData(response.data.data);
    } catch (error) {
      console.error("Failed to calculate price:", error);
      // Only show error if user is on step 2
      if (currentStep === 2) {
        toast.error(t("common:errors.genericErrorTitle"), {
          description: t("forms:slotReservation.priceCalculationError"),
        });
      }
    } finally {
      setIsLoadingPrice(false);
    }
  }, 1000);

  useEffect(() => {
    setIsLoadingPrice(true);
    fetchPrice();
  }, [subjectId, curriculumLevelId, slot, workspaceInformation]);

  if (!slot) return null;
  if (!workspaceInformation) return null;

  const startDateTime = parseISO(slot.startTimeDateTimeString);
  const endDateTime = parseISO(slot.endTimeDateTimeString);

  const handleNextStep = async () => {
    const result = await form.trigger(["subject_id", "curriculum_level_id"]);

    if (result) {
      setCurrentStep(2);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const onSubmit = async (data: SlotReservationFormData) => {
    setIsSubmitting(true);
    setHasError(false);
    try {
      await axiosApiInstance.post(
        WORKSPACES_API_ENDPOINTS(workspaceInformation.id).STUDENT.RESERVE_SLOT,
        null,
        {
          params: data,
        },
      );

      toast.success(t("common:success"), {
        description: t("forms:slotReservation.successMessage"),
      });
      setToReload(true);

      setIsOpen(false);
    } catch (error: any) {
      console.error(error);
      setHasError(true);

      // Handle specific error codes
      if (error.response?.data?.code === "tutor_reservations_disabled") {
        toast.error(t("common:error"), {
          description: t("forms:slotReservation.reservationsDisabledError"),
        });
      } else if (error.response?.data?.code === "insufficient_credits_for_lesson") {
        toast.error(t("common:error"), {
          description:
            error.response?.data?.message || t("forms:slotReservation.insufficientCreditsError"),
        });
      } else {
        toast.error(t("common:error"), {
          description: t("forms:slotReservation.errorMessage"),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ScrollArea className="h-full max-h-[calc(100dvh-3rem)]">
      <Form {...form}>
        <form className="sm:px-6" onSubmit={form.handleSubmit(onSubmit)}>
          <MotionCard
            animate={{ opacity: 1, y: 0 }}
            className="bg-background w-full border-none p-2"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0, y: 100 }}
            transition={{ duration: 0.1 }}
          >
            <CardContent className={"pb-0"}>
              <div className="grid h-fit grid-cols-1 gap-6 md:grid-cols-5">
                {/* Left Column - Constant Information (Green Section) */}
                <div className="flex flex-col gap-3 rounded-lg md:col-span-2">
                  {/* Workspace Data */}
                  <div className="bg-foreground/80 relative flex flex-col items-center justify-center gap-2 overflow-hidden rounded-lg p-4 shadow-md backdrop-blur-sm">
                    <div className="absolute inset-0 overflow-hidden opacity-25">
                      <div className="absolute top-1/2 left-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full bg-transparent">
                        <img
                          alt={workspaceInformation.name}
                          className={"size-full rounded-full blur-2xl"}
                          draggable={false}
                          src={workspaceInformation.logo || "/placeholder.svg"}
                        />
                      </div>
                    </div>
                    <img
                      alt={workspaceInformation.name}
                      className={"size-24 rounded-lg"}
                      src={workspaceInformation.logo || "/placeholder.svg"}
                    />
                    <p className="text-accent-foreground text-sm font-semibold">
                      {workspaceInformation.name}
                    </p>
                  </div>

                  {/* About Reservations */}
                  <div className="border-border rounded-lg border p-3">
                    <div className="flex items-start gap-2">
                      <Icon
                        className="text-muted-foreground mt-0.5 shrink-0"
                        height="1rem"
                        icon="mdi:information"
                        width="1rem"
                      />
                      <div className="text-muted-foreground text-xs">
                        <p>{t("forms:slotReservation.reservationDescription")}</p>
                      </div>
                    </div>
                  </div>

                  {/* Time Frame */}
                  <div className="bg-foreground/80 relative overflow-hidden rounded-lg p-3 backdrop-blur-sm">
                    <div className="absolute inset-0 overflow-hidden opacity-15">
                      <div className="absolute -top-16 -right-16 h-32 w-32 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600" />
                    </div>
                    <h3 className="text-muted-foreground mb-2 text-sm font-semibold">
                      {t("forms:slotReservation.selectedTimeSlot")}
                    </h3>
                    <div className="flex items-start gap-2">
                      <CalendarIcon className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                      <div className={"flex gap-1"}>
                        <p className="text-accent-foreground text-sm font-medium">
                          {format(startDateTime, "PPP", {
                            locale: getDateFnsLocale(i18n.language),
                          })}
                        </p>
                        <p className="text-accent-foreground text-sm">
                          {format(startDateTime, "p", {
                            locale: getDateFnsLocale(i18n.language),
                          })}{" "}
                          -{" "}
                          {format(endDateTime, "p", {
                            locale: getDateFnsLocale(i18n.language),
                          })}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Horizontal Stepper - Improved */}
                  <div className="bg-foreground/80 relative flex-1 overflow-hidden rounded-lg p-4 backdrop-blur-sm">
                    <div className="absolute inset-0 overflow-hidden opacity-15">
                      <div className="absolute -top-[100%] -left-[60%] h-[300%] w-full rounded-full bg-gradient-to-br from-green-400 to-indigo-400" />
                    </div>
                    <div className="flex flex-col space-y-4">
                      {/* Progress Bar */}
                      <div className="bg-muted/30 relative h-1.5 w-full rounded-full">
                        <div
                          className="bg-accent-foreground absolute top-0 left-0 h-full rounded-full transition-all duration-300 ease-in-out"
                          style={{ width: currentStep === 1 ? "50%" : "100%" }}
                        />
                      </div>

                      {/* Steps */}
                      <div className="relative flex items-center justify-between">
                        {/* Step 1 */}
                        <div className="flex flex-col items-center">
                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${
                              currentStep >= 1
                                ? "border-accent-foreground bg-foreground text-accent-foreground"
                                : "border-muted-foreground/30 bg-muted text-muted-foreground"
                            } `}
                          >
                            <Icon height="1.25rem" icon="mdi:book-education" width="1.25rem" />
                          </div>
                          <span
                            className={`mt-2 text-center text-xs font-medium ${currentStep >= 1 ? "text-accent-foreground" : "text-muted-foreground"} `}
                          >
                            {t("forms:slotReservation.lessonDetails")}
                          </span>
                        </div>

                        {/* Step 2 */}
                        <div className="flex flex-col items-center">
                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${
                              currentStep >= 2
                                ? "border-accent-foreground bg-foreground text-accent-foreground"
                                : "border-muted-foreground/30 bg-muted text-muted-foreground"
                            } `}
                          >
                            <Icon height="1.25rem" icon="mdi:cash-check" width="1.25rem" />
                          </div>
                          <span
                            className={`mt-2 text-center text-xs font-medium ${currentStep >= 2 ? "text-accent-foreground" : "text-muted-foreground"} `}
                          >
                            {t("forms:slotReservation.payment")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Step Content Container */}
                <div className="bg-foreground/80 relative h-[480px] overflow-hidden rounded-lg backdrop-blur-sm md:col-span-3">
                  <div className="absolute inset-0 overflow-hidden opacity-15">
                    <motion.div
                      layout
                      className={`absolute ${currentStep === 1 ? "-top-16 -right-16" : "-top-16 -left-16"} -top-16 -right-16 h-32 w-32 rounded-full bg-gradient-to-br from-blue-400 to-purple-600`}
                      transition={{
                        layout: {
                          duration: 0.5,
                        },
                      }}
                    />
                    <motion.div
                      layout
                      className={`absolute ${currentStep === 1 ? "-bottom-12 -left-12" : "-right-12 -bottom-12"} h-24 w-24 rounded-full bg-gradient-to-tr from-green-400 to-emerald-500`}
                      transition={{
                        layout: {
                          duration: 0.5,
                        },
                      }}
                    />
                  </div>
                  <div className="relative h-full">
                    {currentStep === 1 ? (
                      <motion.div
                        key="step1"
                        animate={{ opacity: 1, y: 0 }}
                        className="relative h-full p-4"
                        exit={{ opacity: 0, y: -10 }}
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="mb-4 flex items-center gap-2">
                          <Icon
                            className="text-accent-foreground"
                            height="1.5rem"
                            icon="mdi:book-education"
                            width="1.5rem"
                          />
                          <h3 className="text-accent-foreground text-lg font-semibold">
                            {t("forms:slotReservation.lessonDetails")}
                          </h3>
                        </div>

                        <div className="relative z-50 flex flex-col gap-4">
                          <FormField
                            control={form.control}
                            name="subject_id"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel
                                  className={"text-accent-foreground flex items-center gap-2"}
                                >
                                  <Icon height="1rem" icon="mdi:bookshelf" width="1rem" />
                                  {t("forms:slotReservation.subject")}
                                </FormLabel>
                                <Select
                                  disabled={!subjectsWithLevels || subjectsWithLevels.length === 0}
                                  value={field.value}
                                  onValueChange={field.onChange}
                                >
                                  <FormControl>
                                    <SelectTrigger className="h-10 w-full">
                                      <SelectValue
                                        placeholder={t("forms:slotReservation.subjectPlaceholder")}
                                      >
                                        {field.value && flattenedSubjects ? (
                                          <div className="flex items-center gap-2">
                                            <Icon
                                              height="1rem"
                                              icon={
                                                flattenedSubjects.find(
                                                  (s) => s.subject.id === field.value,
                                                )?.subject.icon || "mdi:bookshelf"
                                              }
                                              width="1rem"
                                            />
                                            <span className="truncate">
                                              {flattenedSubjects.find(
                                                (s) => s.subject.id === field.value,
                                              )?.subject.name || ""}
                                            </span>
                                          </div>
                                        ) : null}
                                      </SelectValue>
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent className="max-w-[90vw]">
                                    {flattenedSubjects.map((subjectItem) => (
                                      <SelectItem
                                        key={subjectItem.subject.id}
                                        className={
                                          "aria-[disabled=true]:text-accent-foreground cursor-pointer! aria-[disabled=true]:opacity-100!"
                                        }
                                        disabled={!subjectItem.isSelectable}
                                        value={subjectItem.subject.id}
                                      >
                                        <div
                                          className={cn(
                                            "flex w-full items-center gap-2",
                                            subjectItem.isChild && "ml-6",
                                          )}
                                        >
                                          <Icon
                                            className="shrink-0"
                                            height="1rem"
                                            icon={subjectItem.subject.icon}
                                            width="1rem"
                                          />
                                          <span
                                            className={cn(
                                              "truncate",
                                              !subjectItem.subject.is_group_subject &&
                                                subjectItem.isChild &&
                                                "text-sm",
                                              subjectItem.subject.is_group_subject && "font-medium",
                                            )}
                                          >
                                            {subjectItem.subject.name}
                                          </span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="curriculum_level_id"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel
                                  className={"text-accent-foreground flex items-center gap-2"}
                                >
                                  <Icon height="1rem" icon="mdi:school" width="1rem" />
                                  {t("forms:slotReservation.curriculumLevel")}
                                </FormLabel>
                                <Select
                                  disabled={!subjectId}
                                  value={field.value}
                                  onValueChange={field.onChange}
                                >
                                  <FormControl>
                                    <SelectTrigger className="h-10 w-full">
                                      <SelectValue
                                        placeholder={
                                          !subjectId
                                            ? t("forms:slotReservation.selectSubjectFirst")
                                            : t("forms:slotReservation.curriculumLevelPlaceholder")
                                        }
                                      >
                                        {field.value && availableCurriculumLevels ? (
                                          <span className="truncate text-start">
                                            {availableCurriculumLevels.find(
                                              (l) => l.id === field.value,
                                            )?.name || ""}
                                          </span>
                                        ) : null}
                                      </SelectValue>
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent className={"max-w-[90vw]"}>
                                    {availableCurriculumLevels.map((level) => (
                                      <SelectItem key={level.id} value={level.id}>
                                        <div className="flex max-w-[80vw] flex-col items-start">
                                          <span className="w-full max-w-full truncate font-medium">
                                            {level.name}
                                          </span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="topic"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel
                                  className={"text-accent-foreground flex items-center gap-2"}
                                >
                                  <Icon height="1rem" icon="mdi:lightbulb" width="1rem" />
                                  {t("forms:slotReservation.topic")}
                                  <span className="text-muted-foreground ml-1 text-xs">
                                    ({t("common:form.optional")})
                                  </span>
                                </FormLabel>
                                <FormControl>
                                  <ScrollArea className="border-input bg-background ring-offset-background focus-within:ring-ring h-20 w-full rounded-xl">
                                    <Textarea
                                      className="[field-sizing:content] min-h-18 resize-none rounded-xl border-none! p-4 text-base break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                                      {...field}
                                      maxLength={255}
                                      placeholder={t("forms:slotReservation.topicPlaceholder")}
                                    />
                                  </ScrollArea>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="context"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel
                                  className={"text-accent-foreground flex items-center gap-2"}
                                >
                                  <Icon height="1rem" icon="mdi:text-box" width="1rem" />
                                  {t("forms:slotReservation.context")}
                                  <span className="text-muted-foreground ml-1 text-xs">
                                    ({t("common:form.optional")})
                                  </span>
                                </FormLabel>
                                <FormControl>
                                  <ScrollArea className="border-input bg-background ring-offset-background focus-within:ring-ring h-20 w-full rounded-xl">
                                    <Textarea
                                      className="[field-sizing:content] min-h-18 resize-none rounded-xl border-none! p-4 text-base break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                                      {...field}
                                      maxLength={500}
                                      placeholder={t("forms:slotReservation.contextPlaceholder")}
                                    />
                                  </ScrollArea>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </motion.div>
                    ) : (
                      /* Step 2: Payment Details (Orange Section) */
                      <motion.div
                        key="step2"
                        animate={{ opacity: 1, y: 0 }}
                        className="relative h-full p-4"
                        exit={{ opacity: 0, y: -10 }}
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3 }}
                      >
                        {isLoadingPrice && <GenericLoader className={"absolute"} />}
                        <div className="mb-4 flex items-center gap-2">
                          <Icon
                            className="text-accent-foreground"
                            height="1.5rem"
                            icon="mdi:cash-check"
                            width="1.5rem"
                          />
                          <h3 className="text-accent-foreground text-lg font-semibold">
                            {t("forms:slotReservation.payment")}
                          </h3>
                        </div>

                        <div className="space-y-4">
                          {/* Selected Details Summary - Compressed */}
                          <div className="border-border rounded-lg border p-3">
                            <h4 className="text-accent-foreground mb-2 text-sm font-medium">
                              {t("forms:slotReservation.selectedOptions")}
                            </h4>

                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="flex items-start gap-2">
                                <Icon
                                  className="text-muted-foreground mt-0.5 shrink-0"
                                  height="1rem"
                                  icon="mdi:bookshelf"
                                  width="1rem"
                                />
                                <div className="min-w-0">
                                  <p className="text-muted-foreground text-xs">
                                    {t("forms:slotReservation.subject")}
                                  </p>
                                  <p className="text-accent-foreground truncate font-medium">
                                    {(() => {
                                      const findSubject = (items: SubjectWithLevels[]): string => {
                                        for (const item of items) {
                                          if (item.subject.id === subjectId)
                                            return item.subject.name;
                                          if (item.children) {
                                            const found = findSubject(item.children);

                                            if (found) return found;
                                          }
                                        }

                                        return "-";
                                      };

                                      return findSubject(subjectsWithLevels || []);
                                    })()}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-start gap-2">
                                <Icon
                                  className="text-muted-foreground mt-0.5 shrink-0"
                                  height="1rem"
                                  icon="mdi:school"
                                  width="1rem"
                                />
                                <div className="min-w-0">
                                  <p className="text-muted-foreground text-xs">
                                    {t("forms:slotReservation.curriculumLevel")}
                                  </p>
                                  <p className="text-accent-foreground truncate font-medium">
                                    {availableCurriculumLevels.find(
                                      (l) => l.id === curriculumLevelId,
                                    )?.name || "-"}
                                  </p>
                                </div>
                              </div>

                              {form.watch("topic") && (
                                <div className="col-span-2 flex items-start gap-2">
                                  <Icon
                                    className="text-muted-foreground mt-0.5 shrink-0"
                                    height="1rem"
                                    icon="mdi:lightbulb"
                                    width="1rem"
                                  />
                                  <div className="flex min-w-0 items-center gap-2">
                                    <p className="text-muted-foreground text-xs">
                                      {t("forms:slotReservation.topic")}
                                    </p>
                                    <p className="text-accent-foreground truncate">
                                      {form.watch("topic")}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Price Breakdown */}
                          {isLoadingPrice ? (
                            <div className="bg-foreground/5 h-[200px] rounded-lg p-4">
                              <div className="flex items-center justify-center py-4">
                                <div className="h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                <span className="text-muted-foreground ml-2">
                                  {t("forms:slotReservation.calculatingPrice")}
                                </span>
                              </div>
                            </div>
                          ) : priceData ? (
                            <div className="bg-foreground/5 rounded-lg p-4">
                              <h4 className="text-accent-foreground mb-3 flex items-center gap-2 text-sm font-medium">
                                <Icon height="1rem" icon="tabler:receipt" width="1rem" />
                                {t("forms:slotReservation.priceBreakdown")}
                              </h4>

                              <div className="space-y-2 text-sm">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Icon
                                      className="text-muted-foreground"
                                      height="1rem"
                                      icon="mdi:clock-outline"
                                      width="1rem"
                                    />
                                    <span className="text-muted-foreground">
                                      {t("forms:slotReservation.duration")}
                                    </span>
                                  </div>
                                  <span className="text-accent-foreground font-medium">
                                    {formatDuration(
                                      { minutes: priceData.duration_minutes },
                                      { locale: currentLanguageLocale() },
                                    )}
                                  </span>
                                </div>

                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Icon
                                      className="text-muted-foreground"
                                      height="1rem"
                                      icon="mdi:cash-multiple"
                                      width="1rem"
                                    />
                                    <span className="text-muted-foreground">
                                      {t("forms:slotReservation.hourlyRate")}
                                    </span>
                                  </div>
                                  <span className="text-accent-foreground font-medium">
                                    {priceData.hourly_rate_formatted}
                                  </span>
                                </div>

                                <div className="mt-2 border-t pt-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">
                                      {t("forms:slotReservation.subtotal")}
                                    </span>
                                    <span className="text-accent-foreground">
                                      {priceData.subtotal_formatted}
                                    </span>
                                  </div>

                                  <div className="mt-1 flex items-center justify-between">
                                    <span className="text-muted-foreground">
                                      {t("forms:slotReservation.serviceFee")}
                                    </span>
                                    <span className="text-accent-foreground">
                                      {priceData.service_fee_formatted}
                                    </span>
                                  </div>

                                  <div className="mt-2 flex items-center justify-between border-t pt-2">
                                    <span className="text-accent-foreground flex items-center gap-1 font-semibold">
                                      <Icon height="1rem" icon="mdi:cash-check" width="1rem" />
                                      {t("forms:slotReservation.total")}
                                    </span>
                                    <span className="text-accent-foreground text-lg font-bold">
                                      {priceData.total_formatted}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="bg-destructive/10 rounded-lg p-4">
                              <div className="text-destructive flex items-center gap-2">
                                <Icon height="1.25rem" icon="mdi:alert-circle" width="1.25rem" />
                                <p className="text-sm">
                                  {t("forms:slotReservation.priceCalculationError")}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Terms Checkbox */}
                          <FormField
                            control={form.control}
                            name="acceptTerms"
                            render={({ field }) => (
                              <FormItem className="text-accent-foreground border-input mt-auto flex h-full flex-row items-center gap-3 rounded-md px-4 py-0 text-start">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value}
                                    className={"border-accent-foreground/60 m-0 h-5 w-5 border"}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                                <div className="space-y-1 text-start text-xs leading-none">
                                  <FormLabel className={"text-xs"}>
                                    {t("forms:labels.agreeOnTerms")}
                                  </FormLabel>
                                  <FormMessage className={"text-xs"} />
                                </div>
                              </FormItem>
                            )}
                          />
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex justify-between gap-3 pt-4">
              {currentStep === 1 ? (
                <>
                  <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                    {t("common:cancel")}
                  </Button>
                  <Button
                    className={"min-w-[calc(60%_-_0.5rem)]"}
                    disabled={!subjectId || !curriculumLevelId}
                    type="button"
                    variant={"foreground"}
                    onClick={handleNextStep}
                  >
                    <div className="flex items-center gap-2">
                      <Icon height={"1rem"} icon="mdi:arrow-right" width={"1rem"} />
                      {t("common:next")}
                    </div>
                  </Button>
                </>
              ) : (
                <>
                  <Button type="button" variant="outline" onClick={handlePreviousStep}>
                    <div className="flex items-center gap-2">
                      <Icon height={"1rem"} icon="mdi:arrow-left" width={"1rem"} />
                      {t("common:back")}
                    </div>
                  </Button>
                  <Button
                    className={"min-w-[calc(60%_-_0.5rem)]"}
                    disabled={
                      isSubmitting ||
                      !form.formState.isValid ||
                      !priceData ||
                      !workspaceInformation?.can_make_reservations ||
                      hasError
                    }
                    type="submit"
                    variant={"foreground"}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        {t("common:processing")}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Icon height={"1rem"} icon="lucide:credit-card" width={"1rem"} />
                        {priceData
                          ? `${t("common:proceedToPayment")} (${priceData.total_formatted})`
                          : t("common:proceedToPayment")}
                      </div>
                    )}
                  </Button>
                </>
              )}
            </CardFooter>
          </MotionCard>
        </form>
      </Form>
    </ScrollArea>
  );
}
